import { action, makeObservable, observable } from "mobx";
import SwayyOrderAPI from "../api/SwayyOrderAPI";
import SwayyShipmentAPI from "../api/SwayyShipmentAPI";
import SwayyInvoiceAPI from "../api/SwayyInvoiceAPI";
import SwayyImageAPI from "../api/SwayyImageAPI";
import { CancelSwayyOrderResult } from "../../../models/SwayyOrder/SwayyOrder";
import {
  SwayyOrderDetail,
  SwayyOrderNote,
} from "../../../models/SwayyOrder/SwayyOrderDetail";
import {
  CreateSwayyShipmentPayload,
  SwayyShipment,
  UpdateSwayyShipmentPayload,
} from "../../../models/SwayyShipment/SwayyShipment";
import {
  CreateSwayyCreditNotePayload,
  SwayyCreditNote,
  SwayyInvoice,
} from "../../../models/SwayyInvoice/SwayyInvoice";
import { userStore } from "../../../stores/sharedStores";
import type { SwayyOrderStore } from "./SwayyOrderStore";
import { swayyCarrierStore } from "./SwayyCarrierStore";
import { swayyWarehouseStore } from "./SwayyWarehouseStore";

const NOTE_IMAGE_MAX_BYTES = 1 * 1024 * 1024;

export class SwayyOrderDetailStore {
  swayyOrderAPI: SwayyOrderAPI;
  swayyShipmentAPI: SwayyShipmentAPI;
  swayyInvoiceAPI: SwayyInvoiceAPI;
  swayyImageAPI: SwayyImageAPI;
  orderListStore: SwayyOrderStore | null;
  order: SwayyOrderDetail | null;
  loading: boolean;
  error: string | null;
  cancelDialogOpen: boolean;
  cancelReason: string;
  cancelLoading: boolean;
  cancelError: string | null;
  notes: SwayyOrderNote[];
  notesLoading: boolean;
  notesError: string | null;
  noteSaving: boolean;
  selectedProduct: number | null;
  selectedProductImages: string[];
  popupState: {
    openImageGallery: boolean;
    openCreateShipment: boolean;
    openUpdateShipment: boolean;
    openDeleteShipment: boolean;
    openCreditNote: boolean;
  };

  invoices: SwayyInvoice[];
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoiceActionLoading: boolean;

  creditNotesByInvoice: Record<number, SwayyCreditNote[]>;
  creditNotesLoadingId: number | null;
  creditNoteSaving: boolean;
  creditNoteError: string | null;
  creditNoteInvoiceId: number | null;
  creditNoteReason: string;
  creditNoteRemarks: string;
  creditNoteAmount: string;

  lookupsLoading: boolean;

  shipmentSaving: boolean;
  shipmentError: string | null;
  selectedShipmentId: number | null;
  selectedShipment: SwayyShipment | null;

  constructor(orderListStore?: SwayyOrderStore) {
    this.swayyOrderAPI = new SwayyOrderAPI();
    this.swayyShipmentAPI = new SwayyShipmentAPI();
    this.swayyInvoiceAPI = new SwayyInvoiceAPI();
    this.swayyImageAPI = new SwayyImageAPI();
    this.orderListStore = orderListStore ?? null;
    this.order = null;
    this.loading = false;
    this.error = null;
    this.cancelDialogOpen = false;
    this.cancelReason = "";
    this.cancelLoading = false;
    this.cancelError = null;
    this.notes = [];
    this.notesLoading = false;
    this.notesError = null;
    this.noteSaving = false;
    this.selectedProduct = null;
    this.selectedProductImages = [];
    this.popupState = {
      openImageGallery: false,
      openCreateShipment: false,
      openUpdateShipment: false,
      openDeleteShipment: false,
      openCreditNote: false,
    };
    this.invoices = [];
    this.invoicesLoading = false;
    this.invoicesError = null;
    this.invoiceActionLoading = false;
    this.creditNotesByInvoice = {};
    this.creditNotesLoadingId = null;
    this.creditNoteSaving = false;
    this.creditNoteError = null;
    this.creditNoteInvoiceId = null;
    this.creditNoteReason = "refund";
    this.creditNoteRemarks = "";
    this.creditNoteAmount = "";
    this.lookupsLoading = false;
    this.shipmentSaving = false;
    this.shipmentError = null;
    this.selectedShipmentId = null;
    this.selectedShipment = null;

    makeObservable(this, {
      order: observable,
      loading: observable,
      error: observable,
      cancelDialogOpen: observable,
      cancelReason: observable,
      cancelLoading: observable,
      cancelError: observable,
      notes: observable,
      notesLoading: observable,
      notesError: observable,
      noteSaving: observable,
      selectedProduct: observable,
      selectedProductImages: observable,
      popupState: observable,
      invoices: observable,
      invoicesLoading: observable,
      invoicesError: observable,
      invoiceActionLoading: observable,
      creditNotesByInvoice: observable,
      creditNotesLoadingId: observable,
      creditNoteSaving: observable,
      creditNoteError: observable,
      creditNoteInvoiceId: observable,
      creditNoteReason: observable,
      creditNoteRemarks: observable,
      creditNoteAmount: observable,
      lookupsLoading: observable,
      shipmentSaving: observable,
      shipmentError: observable,
      selectedShipmentId: observable,
      selectedShipment: observable,
      getOrderDetail: action,
      loadInvoices: action,
      loadNotes: action,
      loadLookups: action,
      patchOrder: action,
      upsertInvoiceFromShipment: action,
      openCancelDialog: action,
      closeCancelDialog: action,
      setCancelReason: action,
      confirmCancelOrder: action,
      addNote: action,
      updateNote: action,
      deleteNote: action,
      uploadNoteImages: action,
      setSelectedProductImages: action,
      managePopup: action,
      openCreateShipment: action,
      openUpdateShipment: action,
      openDeleteShipment: action,
      createShipment: action,
      updateShipment: action,
      deleteShipment: action,
      regenerateInvoice: action,
      openCreditNote: action,
      setCreditNoteReason: action,
      setCreditNoteRemarks: action,
      setCreditNoteAmount: action,
      loadCreditNotes: action,
      refreshLoadedCreditNotes: action,
      createCreditNote: action,
      reset: action,
    });
  }

  getOrderDetail = async (
    orderId: number | string,
    options?: {
      silent?: boolean;
      /** Load notes — default true on full load, false on silent refresh */
      withNotes?: boolean;
      /** Load invoices — default true on full load, false on silent refresh */
      withInvoices?: boolean;
    },
  ) => {
    const silent = options?.silent === true;
    const withNotes = options?.withNotes ?? !silent;
    const withInvoices = options?.withInvoices ?? !silent;
    if (!silent) {
      this.loading = true;
      this.error = null;
    }
    try {
      const order = await this.swayyOrderAPI.getOrderDetail(orderId);
      this.order = order;
      this.orderListStore?.patchOrder(order.id, {
        status: order.status,
        payment_status: order.payment_status,
      });
      if (withInvoices) void this.loadInvoices(orderId);
      if (withNotes) void this.loadNotes(orderId);
      return order;
    } catch (error: any) {
      if (!silent) {
        this.error = error?.message || "Failed to load order details";
      }
      throw error;
    } finally {
      if (!silent) this.loading = false;
    }
  };

  loadInvoices = async (
    orderId?: number | string,
    options?: { silent?: boolean },
  ) => {
    const id = orderId ?? this.order?.id;
    if (!id) return;

    const silent = options?.silent === true;
    if (!silent) {
      this.invoicesLoading = true;
      this.invoicesError = null;
    }
    try {
      this.invoices = await this.swayyInvoiceAPI.getOrderInvoices(id);
      const generatedIds = this.invoices
        .filter(
          (invoice) =>
            invoice.type !== "credit_note" &&
            String(invoice.status || "").toLowerCase() === "generated",
        )
        .map((invoice) => invoice.id);
      await Promise.all(
        generatedIds.map((invoiceId) =>
          this.loadCreditNotes(invoiceId, { silent: true }).catch(() => null),
        ),
      );
    } catch (error: any) {
      if (!silent) {
        this.invoicesError = error?.message || "Failed to load invoices";
      }
      throw error;
    } finally {
      if (!silent) this.invoicesLoading = false;
    }
  };

  loadNotes = async (orderId?: number | string) => {
    const id = orderId ?? this.order?.id;
    if (!id) return;

    this.notesLoading = true;
    this.notesError = null;
    try {
      this.notes = await this.swayyOrderAPI.getOrderNotes(id);
    } catch (error: any) {
      this.notesError = error?.message || "Failed to load notes";
      throw error;
    } finally {
      this.notesLoading = false;
    }
  };

  loadLookups = async () => {
    if (
      swayyWarehouseStore.warehouses.length &&
      swayyCarrierStore.carriers.length
    ) {
      return;
    }
    this.lookupsLoading = true;
    try {
      await Promise.all([
        swayyWarehouseStore.getWarehouses(),
        swayyCarrierStore.getCarriers(),
      ]);
    } catch (error: any) {
      throw error;
    } finally {
      this.lookupsLoading = false;
    }
  };

  patchOrder = (
    orderId: number | string,
    patch: Partial<SwayyOrderDetail>,
  ) => {
    if (!this.order || String(this.order.id) !== String(orderId)) return;
    this.order = { ...this.order, ...patch };
  };

  /** Keep invoices list in sync when shipment response embeds an invoice ref. */
  upsertInvoiceFromShipment = (shipment: SwayyShipment) => {
    if (!shipment.invoice?.id) return;
    const invoiceId = shipment.invoice.id;
    const existing = this.invoices.find(
      (invoice) => Number(invoice.id) === Number(invoiceId),
    );
    if (existing) {
      this.invoices = this.invoices.map((invoice) =>
        Number(invoice.id) === Number(invoiceId)
          ? {
              ...invoice,
              status: shipment.invoice?.status ?? invoice.status,
              shipment_id: shipment.id,
            }
          : invoice,
      );
      return;
    }

    this.invoices = [
      ...this.invoices,
      {
        id: invoiceId,
        invoice_number: `#${invoiceId}`,
        type: "invoice",
        status: shipment.invoice.status ?? "pending",
        issued_at: null,
        grand_total: 0,
        download_url: null,
        order_id: this.order?.id,
        order_number: this.order?.order_number ?? null,
        shipment_id: shipment.id,
      },
    ];
  };

  openCancelDialog = () => {
    this.cancelReason = "";
    this.cancelError = null;
    this.cancelDialogOpen = true;
  };

  closeCancelDialog = () => {
    if (this.cancelLoading) return;
    this.cancelDialogOpen = false;
    this.cancelReason = "";
    this.cancelError = null;
  };

  setCancelReason = (reason: string) => {
    this.cancelReason = reason;
    if (this.cancelError) this.cancelError = null;
  };

  confirmCancelOrder = async (): Promise<{
    result: CancelSwayyOrderResult;
    orderNumber: string;
  } | null> => {
    if (!this.order || !this.cancelReason.trim()) return null;

    this.cancelLoading = true;
    this.cancelError = null;
    try {
      const orderNumber = this.order.order_number;
      const orderId = this.order.id;
      const reason = this.cancelReason.trim();
      const result = await this.swayyOrderAPI.cancelOrder(orderId, { reason });

      this.patchOrder(orderId, {
        status: result.status,
        cancelled_at: this.order.cancelled_at || new Date().toISOString(),
      });
      this.orderListStore?.patchOrder(orderId, { status: result.status });
      this.cancelDialogOpen = false;
      this.cancelReason = "";
      this.cancelError = null;
      return { result, orderNumber };
    } catch (error: any) {
      this.cancelError = error?.message || "Failed to cancel order.";
      throw error;
    } finally {
      this.cancelLoading = false;
    }
  };

  uploadNoteImages = async (files: File[]): Promise<string[]> => {
    if (!files.length) return [];

    const oversized = files.filter((file) => file.size > NOTE_IMAGE_MAX_BYTES);
    if (oversized.length > 0) {
      throw new Error(
        `Image must be 1 MB or less. Rejected: ${oversized
          .map((file) => file.name)
          .join(", ")}`,
      );
    }

    const urls = await this.swayyImageAPI.uploadMultiple(files, "order");
    return (urls ?? []).filter(Boolean);
  };

  addNote = async (
    message: string,
    files: File[] = [],
  ): Promise<SwayyOrderNote | null> => {
    const noteText = message.trim();
    if (!this.order || !noteText) return null;

    this.noteSaving = true;
    try {
      const uploadedUrls = await this.uploadNoteImages(files);
      const created = await this.swayyOrderAPI.addOrderNote(this.order.id, {
        note: noteText,
        ...(uploadedUrls.length > 0 ? { image_urls: uploadedUrls } : {}),
      });

      const nextNote: SwayyOrderNote = {
        id: created.id ?? `local-${Date.now()}`,
        note: created.note || noteText,
        images:
          created.images ??
          uploadedUrls.map((url) => ({ url })),
        is_edited: Boolean(created.is_edited),
        is_deleted: Boolean(created.is_deleted),
        can_modify: created.can_modify ?? true,
        created_at: created.created_at || new Date().toISOString(),
        updated_at: created.updated_at ?? null,
        deleted_at: created.deleted_at ?? null,
        created_by: created.created_by ?? userStore.user?.name ?? null,
        updated_by: created.updated_by ?? null,
        deleted_by: created.deleted_by ?? null,
      };

      this.notes = [nextNote, ...this.notes];
      return nextNote;
    } catch (error: any) {
      throw error;
    } finally {
      this.noteSaving = false;
    }
  };

  updateNote = async (
    noteId: number | string,
    message: string,
    files: File[] = [],
    keepImageUrls: string[] = [],
  ): Promise<SwayyOrderNote | null> => {
    const noteText = message.trim();
    if (!this.order || !noteText) return null;

    this.noteSaving = true;
    try {
      const uploadedUrls = await this.uploadNoteImages(files);
      const imageUrls = [...keepImageUrls, ...uploadedUrls];
      const updated = await this.swayyOrderAPI.updateOrderNote(
        this.order.id,
        noteId,
        {
          note: noteText,
          image_urls: imageUrls,
        },
      );

      const nextNote: SwayyOrderNote = {
        id: updated.id ?? noteId,
        note: updated.note || noteText,
        images:
          updated.images ?? imageUrls.map((url) => ({ url })),
        is_edited: true,
        is_deleted: Boolean(updated.is_deleted),
        can_modify: updated.can_modify ?? true,
        created_at: updated.created_at || new Date().toISOString(),
        updated_at: updated.updated_at ?? new Date().toISOString(),
        deleted_at: updated.deleted_at ?? null,
        created_by: updated.created_by ?? null,
        updated_by: updated.updated_by ?? userStore.user?.name ?? null,
        deleted_by: updated.deleted_by ?? null,
      };

      this.notes = this.notes.map((note) =>
        String(note.id) === String(noteId) ? nextNote : note,
      );
      return nextNote;
    } catch (error: any) {
      throw error;
    } finally {
      this.noteSaving = false;
    }
  };

  deleteNote = async (noteId: number | string): Promise<SwayyOrderNote | null> => {
    if (!this.order) return null;

    this.noteSaving = true;
    try {
      const deleted = await this.swayyOrderAPI.deleteOrderNote(
        this.order.id,
        noteId,
      );
      const nextNote: SwayyOrderNote = {
        id: deleted.id ?? noteId,
        note: "",
        images: [],
        is_edited: Boolean(deleted.is_edited),
        is_deleted: true,
        can_modify: false,
        created_at: deleted.created_at || new Date().toISOString(),
        updated_at: deleted.updated_at ?? new Date().toISOString(),
        deleted_at: deleted.deleted_at ?? new Date().toISOString(),
        created_by: deleted.created_by ?? null,
        updated_by: deleted.updated_by ?? null,
        deleted_by: deleted.deleted_by ?? userStore.user?.name ?? null,
      };
      this.notes = this.notes.map((note) =>
        String(note.id) === String(noteId) ? nextNote : note,
      );
      return nextNote;
    } catch (error: any) {
      throw error;
    } finally {
      this.noteSaving = false;
    }
  };

  setSelectedProductImages = (productId: number, images: string[]) => {
    this.selectedProduct = productId;
    this.selectedProductImages = images.filter(Boolean);
  };

  managePopup = (
    popup: keyof typeof this.popupState,
    actionType: "open" | "close",
  ) => {
    const nextOpen = actionType === "open";
    if (this.popupState[popup] === nextOpen) return;

    this.popupState[popup] = nextOpen;

    if (actionType === "close") {
      if (popup === "openImageGallery") {
        this.selectedProduct = null;
        this.selectedProductImages = [];
      }
      if (
        popup === "openCreateShipment" ||
        popup === "openUpdateShipment" ||
        popup === "openDeleteShipment"
      ) {
        this.shipmentError = null;
        this.selectedShipmentId = null;
        this.selectedShipment = null;
      }
      if (popup === "openCreditNote") {
        this.creditNoteError = null;
        this.creditNoteInvoiceId = null;
        this.creditNoteReason = "refund";
        this.creditNoteRemarks = "";
        this.creditNoteAmount = "";
      }
    }
  };

  openCreateShipment = async () => {
    this.shipmentError = null;
    try {
      await this.loadLookups();
      this.managePopup("openCreateShipment", "open");
    } catch (error: any) {
      this.shipmentError = error?.message || "Failed to load warehouses/carriers";
      throw error;
    }
  };

  openUpdateShipment = async (shipmentId: number) => {
    this.shipmentError = null;
    this.selectedShipmentId = shipmentId;
    try {
      await this.loadLookups();
      const existing = this.order?.shipments.find(
        (shipment) => shipment.id === shipmentId,
      );
      if (!existing) {
        throw new Error("Shipment not found on this order.");
      }

      const carrierName = (existing.carrier || "").trim().toLowerCase();
      const matchedCarrier = carrierName
        ? swayyCarrierStore.carriers.find(
            (carrier) => carrier.name.trim().toLowerCase() === carrierName,
          )
        : undefined;

      this.selectedShipment = {
        id: existing.id,
        shipment_number: existing.shipment_number ?? null,
        order_id: this.order!.id,
        carrier: existing.carrier ?? null,
        carrier_id: matchedCarrier?.id ?? null,
        awb: existing.awb ?? null,
        tracking_url: existing.tracking_url ?? null,
        status: existing.status || "created",
        shipped_at: existing.shipped_at ?? null,
        expected_at: existing.expected_at ?? null,
        delivered_at: existing.delivered_at ?? null,
        items: (existing.items ?? []).map((item) => ({
          order_item_id: item.order_item_id,
          qty: item.qty,
          sku: item.sku,
          name: item.name,
          variant_label: item.variant_label ?? null,
        })),
        invoice: existing.invoice
          ? {
              id: existing.invoice.id,
              status: existing.invoice.status || "",
            }
          : null,
      };
      this.managePopup("openUpdateShipment", "open");
    } catch (error: any) {
      this.shipmentError = error?.message || "Failed to open shipment update";
      throw error;
    }
  };

  openDeleteShipment = (shipmentId: number) => {
    this.selectedShipmentId = shipmentId;
    this.shipmentError = null;
    this.managePopup("openDeleteShipment", "open");
  };

  createShipment = async (body: CreateSwayyShipmentPayload) => {
    if (!this.order) return null;
    this.shipmentSaving = true;
    this.shipmentError = null;
    try {
      const created = await this.swayyShipmentAPI.createOrderShipment(
        this.order.id,
        body,
      );
      await this.getOrderDetail(this.order.id, {
        silent: true,
        withNotes: false,
        withInvoices: true,
      });
      this.managePopup("openCreateShipment", "close");
      return created;
    } catch (error: any) {
      this.shipmentError = error?.message || "Failed to create shipment";
      throw error;
    } finally {
      this.shipmentSaving = false;
    }
  };

  updateShipment = async (body: UpdateSwayyShipmentPayload) => {
    if (!this.order || !this.selectedShipmentId) return null;
    this.shipmentSaving = true;
    this.shipmentError = null;
    try {
      const updated = await this.swayyShipmentAPI.updateShipment(
        this.selectedShipmentId,
        body,
      );
      await this.getOrderDetail(this.order.id, {
        silent: true,
        withNotes: false,
        withInvoices: true,
      });
      this.upsertInvoiceFromShipment(updated);
      this.managePopup("openUpdateShipment", "close");
      this.selectedShipmentId = null;
      this.selectedShipment = null;
      return updated;
    } catch (error: any) {
      this.shipmentError = error?.message || "Failed to update shipment";
      throw error;
    } finally {
      this.shipmentSaving = false;
    }
  };

  deleteShipment = async () => {
    if (!this.order || !this.selectedShipmentId) return null;
    this.shipmentSaving = true;
    this.shipmentError = null;
    try {
      const shipmentId = this.selectedShipmentId;
      await this.swayyShipmentAPI.deleteShipment(shipmentId);
      await this.getOrderDetail(this.order.id, {
        silent: true,
        withNotes: false,
        withInvoices: true,
      });
      this.managePopup("openDeleteShipment", "close");
      this.selectedShipmentId = null;
      return true;
    } catch (error: any) {
      this.shipmentError = error?.message || "Failed to delete shipment";
      throw error;
    } finally {
      this.shipmentSaving = false;
    }
  };

  regenerateInvoice = async (
    documentId: number,
    options?: { againstInvoiceId?: number },
  ) => {
    this.invoiceActionLoading = true;
    try {
      const result = await this.swayyInvoiceAPI.regenerateInvoice(documentId);
      if (this.order) {
        await this.loadInvoices(this.order.id, { silent: true });
      }
      if (options?.againstInvoiceId != null) {
        await this.loadCreditNotes(options.againstInvoiceId, { silent: true });
      } else {
        await this.refreshLoadedCreditNotes({ silent: true });
      }
      return result;
    } catch (error: any) {
      throw error;
    } finally {
      this.invoiceActionLoading = false;
    }
  };

  openCreditNote = (invoiceId: number) => {
    this.creditNoteInvoiceId = invoiceId;
    this.creditNoteReason = "refund";
    this.creditNoteRemarks = "";
    this.creditNoteAmount = "";
    this.creditNoteError = null;
    this.managePopup("openCreditNote", "open");
  };

  setCreditNoteReason = (reason: string) => {
    this.creditNoteReason = reason;
  };

  setCreditNoteRemarks = (remarks: string) => {
    this.creditNoteRemarks = remarks;
  };

  setCreditNoteAmount = (amount: string) => {
    this.creditNoteAmount = amount;
  };

  loadCreditNotes = async (
    invoiceId: number,
    options?: { silent?: boolean },
  ) => {
    const silent = options?.silent === true;
    if (!silent) this.creditNotesLoadingId = invoiceId;
    try {
      const notes = await this.swayyInvoiceAPI.getInvoiceCreditNotes(invoiceId);
      this.creditNotesByInvoice = {
        ...this.creditNotesByInvoice,
        [invoiceId]: notes,
      };
      return notes;
    } catch (error: any) {
      throw error;
    } finally {
      if (!silent) this.creditNotesLoadingId = null;
    }
  };

  refreshLoadedCreditNotes = async (options?: { silent?: boolean }) => {
    const invoiceIds = Object.keys(this.creditNotesByInvoice).map(Number);
    await Promise.all(
      invoiceIds.map((invoiceId) =>
        this.loadCreditNotes(invoiceId, options).catch(() => null),
      ),
    );
  };

  createCreditNote = async () => {
    if (!this.creditNoteInvoiceId || !this.creditNoteReason) return null;
    const amount = Number(this.creditNoteAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.creditNoteError = "Enter a valid credit note amount.";
      return null;
    }

    this.creditNoteSaving = true;
    this.creditNoteError = null;
    try {
      const invoiceId = this.creditNoteInvoiceId;
      const payload: CreateSwayyCreditNotePayload = {
        reason: this.creditNoteReason,
        remarks: this.creditNoteRemarks.trim() || undefined,
        amount,
      };
      const result = await this.swayyInvoiceAPI.createCreditNote(
        invoiceId,
        payload,
      );
      // Refresh only this invoice's credit notes — keep order/notes untouched
      await this.loadCreditNotes(invoiceId, { silent: true });
      this.managePopup("openCreditNote", "close");
      return result;
    } catch (error: any) {
      this.creditNoteError = error?.message || "Failed to create credit note";
      throw error;
    } finally {
      this.creditNoteSaving = false;
    }
  };

  reset = () => {
    this.order = null;
    this.loading = false;
    this.error = null;
    this.cancelDialogOpen = false;
    this.cancelReason = "";
    this.cancelLoading = false;
    this.cancelError = null;
    this.notes = [];
    this.notesLoading = false;
    this.notesError = null;
    this.noteSaving = false;
    this.selectedProduct = null;
    this.selectedProductImages = [];
    this.popupState = {
      openImageGallery: false,
      openCreateShipment: false,
      openUpdateShipment: false,
      openDeleteShipment: false,
      openCreditNote: false,
    };
    this.invoices = [];
    this.invoicesLoading = false;
    this.invoicesError = null;
    this.invoiceActionLoading = false;
    this.creditNotesByInvoice = {};
    this.creditNotesLoadingId = null;
    this.creditNoteSaving = false;
    this.creditNoteError = null;
    this.creditNoteInvoiceId = null;
    this.creditNoteReason = "refund";
    this.creditNoteRemarks = "";
    this.creditNoteAmount = "";
    this.shipmentSaving = false;
    this.shipmentError = null;
    this.selectedShipmentId = null;
    this.selectedShipment = null;
  };
}
